import React, { useEffect, useRef, useState } from "react";
import { runNrql } from "./nerdgraph";

/**
 * Hook form of the SDK's NrqlQuery. Returns { data, loading, error } where
 * `data` is chart data (see nerdgraph.js#toChartData).
 */
export function useNrqlQuery({ accountId, query, pollInterval, skip = false }) {
  const [state, setState] = useState({
    data: null,
    loading: !skip && Boolean(accountId && query),
    error: null,
  });

  // Kept in a ref so the poll timer never re-arms just because a render happened.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (skip || !accountId || !query) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let controller = null;

    const fetchOnce = async (isRefresh) => {
      controller = new AbortController();

      if (!isRefresh) {
        setState({ data: null, loading: true, error: null });
      }

      try {
        const result = await runNrql({
          accountId,
          query,
          signal: controller.signal,
        });
        if (cancelled) return;
        setState({ data: result.data, loading: false, error: null });
      } catch (error) {
        if (cancelled || error?.name === "AbortError") return;
        // On a poll failure keep the last good data visible, as the SDK does.
        setState({
          data: isRefresh ? stateRef.current.data : null,
          loading: false,
          error,
        });
      }
    };

    fetchOnce(false);

    if (pollInterval && pollInterval > 0) {
      timer = setInterval(() => fetchOnce(true), pollInterval);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (controller) controller.abort();
    };
  }, [accountId, query, pollInterval, skip]);

  return state;
}

/**
 * Render-prop component matching the SDK's NrqlQuery:
 *   <NrqlQuery accountId={id} query={nrql}>{({data, loading, error}) => ...}</NrqlQuery>
 */
export function NrqlQuery({ accountId, query, pollInterval, skip, children }) {
  const result = useNrqlQuery({ accountId, query, pollInterval, skip });
  if (typeof children === "function") return children(result);
  return children ?? null;
}

/**
 * Imperative form. Like the SDK, this resolves rather than rejects on failure —
 * callers inspect `error` (or fall back to `data || []`).
 */
NrqlQuery.query = async ({ accountId, query }) => {
  try {
    const result = await runNrql({ accountId, query });
    return { data: result.data, error: null, metadata: result.metadata };
  } catch (error) {
    console.error("[nr1-compat] NRQL query failed:", error);
    return { data: null, error };
  }
};

NrqlQuery.FORMAT_TYPE = { RAW: "raw", CHART: "chart" };
NrqlQuery.CHART_TYPE = {
  AREA: "area",
  BAR: "bar",
  BILLBOARD: "billboard",
  LINE: "line",
  PIE: "pie",
  TABLE: "table",
};

/**
 * Convenience hook kept for API parity — the nerdlet source imports `useQuery`
 * from "nr1", and a missing named export is a hard bundler error.
 */
export function useQuery({ query, accountId, pollInterval, skip }) {
  return useNrqlQuery({ accountId, query, pollInterval, skip });
}

export const NerdGraphQuery = {
  query: async () => {
    throw new Error(
      "[nr1-compat] NerdGraphQuery is not implemented in the standalone build."
    );
  },
};
